"use client";

import { useLayoutEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import CommentAdd01Icon from "@hugeicons/core-free-icons/CommentAdd01Icon";
import InstagramIcon from "@hugeicons/core-free-icons/InstagramIcon";
import LanguageSquareIcon from "@hugeicons/core-free-icons/LanguageSquareIcon";
import SlidersHorizontalIcon from "@hugeicons/core-free-icons/SlidersHorizontalIcon";
import UserMultiple02Icon from "@hugeicons/core-free-icons/UserMultiple02Icon";

const textBadges = [
  {
    label: "Creators",
    className: "left-[5%] top-[12%] sm:left-[8%] sm:top-[7%]",
    textColor: "text-pink-600 dark:text-pink-400",
    tilt: -10,
  },
  {
    label: "Influencers",
    className: "right-[5%] top-[14%] sm:right-[6%] sm:top-[9%]",
    textColor: "text-violet-600 dark:text-violet-400",
    tilt: 9,
  },
  {
    label: "Businesses",
    className: "left-[4%] bottom-[14%] sm:left-[10%] sm:bottom-[10%]",
    textColor: "text-sky-600 dark:text-sky-400",
    tilt: -8,
  },
  {
    label: "Agencies",
    className: "right-[4%] bottom-[15%] sm:right-[9%] sm:bottom-[11%]",
    textColor: "text-emerald-600 dark:text-emerald-400",
    tilt: 8,
  },
  {
    label: "Startups",
    className: "left-1/2 bottom-[8%] -translate-x-1/2 sm:bottom-[8%]",
    textColor: "text-orange-600 dark:text-orange-400",
    tilt: -6,
  },
] as const;

const iconBadges = [
  { icon: InstagramIcon, className: "left-[2%] top-[45%] sm:left-[2%] sm:top-[42%]", iconColor: "text-pink-500 dark:text-pink-400", tilt: -12 },
  { icon: UserMultiple02Icon, className: "right-[3%] top-[38%] sm:right-[6%] sm:top-[33%]", iconColor: "text-violet-500 dark:text-violet-400", tilt: 10 },
  { icon: SlidersHorizontalIcon, className: "left-[42%] top-[8%] sm:left-[43%] sm:top-[3%]", iconColor: "text-sky-500 dark:text-sky-400", tilt: 2 },
  {
    icon: CommentAdd01Icon,
    mobileKey: "chat" as const,
    className: "sm:right-[28%] sm:bottom-[10%]",
    iconColor: "text-emerald-500 dark:text-emerald-400",
    tilt: 12,
  },
  {
    icon: LanguageSquareIcon,
    mobileKey: "language" as const,
    className: "sm:right-[22%] sm:top-[8%]",
    iconColor: "text-orange-500 dark:text-orange-400",
    tilt: -10,
  },
] as const;

/** Mobile (default, below sm): language/chat sit in mid sides, away from corner pills. */
const MOBILE_LANGUAGE_POSITIONS = [
  "left-[2%] top-[30%]",
  "left-[4%] top-[38%]",
  "left-[1%] top-[44%]",
  "left-[6%] top-[34%]",
] as const;

const MOBILE_CHAT_POSITIONS = [
  "right-[3%] top-[36%]",
  "right-[4%] top-[44%]",
  "right-[2%] top-[50%]",
  "right-[5%] top-[40%]",
] as const;

function iconBadgePositionClass(
  badge: { className: string; mobileKey?: "language" | "chat" },
  mobileSlots: { lang: number; chat: number },
) {
  if ("mobileKey" in badge && badge.mobileKey === "language") {
    return `${MOBILE_LANGUAGE_POSITIONS[mobileSlots.lang % MOBILE_LANGUAGE_POSITIONS.length]} ${badge.className}`;
  }
  if ("mobileKey" in badge && badge.mobileKey === "chat") {
    return `${MOBILE_CHAT_POSITIONS[mobileSlots.chat % MOBILE_CHAT_POSITIONS.length]} ${badge.className}`;
  }
  return badge.className;
}

export default function BusinessTypes() {
  const [mobileIconSlots, setMobileIconSlots] = useState({ lang: 0, chat: 0 });

  useLayoutEffect(() => {
    setMobileIconSlots({
      lang: Math.floor(Math.random() * MOBILE_LANGUAGE_POSITIONS.length),
      chat: Math.floor(Math.random() * MOBILE_CHAT_POSITIONS.length),
    });
  }, []);

  // Deterministic tilt so SSR and client always match.
  const tiltFromKey = (key: string, spread: number) => {
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
    const normalized = ((Math.abs(hash) % 1000) / 1000) * 2 - 1; // -1..1
    return Number((normalized * spread).toFixed(1));
  };
  const deterministicTextBadges = textBadges.map((badge) => ({
    ...badge,
    tilt: tiltFromKey(`text-${badge.label}`, 12),
  }));
  const deterministicIconBadges = iconBadges.map((badge) => ({
    ...badge,
    tilt: tiltFromKey(
      "mobileKey" in badge ? `icon-${badge.mobileKey}` : `icon-${badge.className}`,
      16,
    ),
  }));

  return (
    <section className="w-full border-t border-border/40 bg-[#FBFBFB] dark:bg-[#101113]">
      <div className="mx-auto w-full max-w-7xl container-padding py-20 sm:py-24">
        <div className="relative mx-auto flex min-h-[18rem] max-w-6xl items-center justify-center sm:min-h-[22rem]">
          {deterministicTextBadges.map((badge, index) => (
            <div
              key={badge.label}
              data-biz-chip
              data-tilt={badge.tilt}
              className={`absolute flex ${index % 2 === 0 ? "animate-float-soft" : "animate-float-soft-delay"} ${badge.className}`}
            >
              <span
                className={`rounded-full border border-black/5 bg-white px-4 py-2.5 text-[1.2rem] font-extrabold leading-none sm:px-8 sm:py-3.5 sm:text-[1.7rem] ${badge.textColor} dark:border-white/10 dark:bg-[#0b0b0d]`}
              >
                {badge.label}
              </span>
            </div>
          ))}

          {deterministicIconBadges.map((badge, idx) => (
            <div
              key={`${badge.className}-${idx}`}
              data-biz-chip
              data-tilt={badge.tilt}
              className={`absolute ${idx <= 1 ? "hidden sm:flex" : "flex"} ${idx % 2 === 0 ? "animate-float-soft-delay-2" : "animate-float-soft"} ${iconBadgePositionClass(badge, mobileIconSlots)}`}
            >
              <span
                className={`inline-flex size-[3rem] items-center justify-center rounded-full border border-black/5 bg-white sm:size-[3.9rem] ${badge.iconColor} dark:border-white/10 dark:bg-[#0b0b0d]`}
              >
                <HugeiconsIcon icon={badge.icon} size={24} color="currentColor" className="sm:hidden" />
                <HugeiconsIcon icon={badge.icon} size={30} color="currentColor" className="hidden sm:block" />
              </span>
            </div>
          ))}

          <h2 data-biz-title className="text-center text-balance text-4xl font-extrabold tracking-tight text-foreground sm:text-7xl">
            Built for everyone posting on Instagram
          </h2>
        </div>
      </div>
    </section>
  );
}
